import React, { memo, useEffect, useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navbar } from 'reactstrap';
import dayjs from 'dayjs';

import announcementsBannerService from '../../services/announcementsBannerService';
import { DASHBOARD_PAGE_PATH } from '../../constants/appPathConst';
import withRoutingProps from '../../hooks/withRoutingProps';
import bn from '../../generalUtils/benames';
import { showMsg } from '../../services/library';

const bem = bn.create('announcementBar');

const AnnouncementBar = ({ location, isPreview = true, barData = {} }) => {
  const QUERY_KEY = useId();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState([]);

  const {
    data: announcements = isPreview ? [barData] : [],
    refetch,
    isFetching
  } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: async () => {
      console.log(barData);
      if (isPreview) {
        return [...barData];
      }
      const res = await announcementsBannerService.getAllAnnoucementsBar();
      return res?.data || [];
    },
    enabled: location.pathname === DASHBOARD_PAGE_PATH,
    refetchOnWindowFocus: false
  });

  const handleDismiss = async id => {
    if (isPreview) {
      return true;
    }
    try {
      await announcementsBannerService.dismissAnnoucementsBar({ ab_id: id });
    } catch (err) {
      showMsg(err, 'error');
    }
    setDismissed(prev => [...prev, id]);
    refetch();
  };

  // Filter out dismissed or expired announcements

  const visibleAnnouncements = announcements?.filter(item => {
    const { ab_id, ab_enddate } = item;
    const isExpired = ab_enddate && dayjs().isAfter(dayjs(new Date(ab_enddate)));
    return isPreview ? true : !dismissed.includes(ab_id) && !isExpired;
  });

  useEffect(() => {
    const checkExpiredAnnouncements = () => {
      announcements.forEach(item => {
        const { ab_id, ab_enddate } = item;
        if (ab_enddate && dayjs().isAfter(dayjs(new Date(ab_enddate)))) {
          handleDismiss(ab_id);
        }
      });
    };

    const intervalId = setInterval(checkExpiredAnnouncements, 1000); // Check every second
    return () => clearInterval(intervalId);
  }, [announcements]);

  useEffect(() => {
    if (visibleAnnouncements?.length > 0) {
      setCurrentIndex(prevIndex => {
        // Adjust the currentIndex if it exceeds the bounds of the updated array
        return prevIndex >= visibleAnnouncements?.length ? 0 : prevIndex;
      });
      const intervalId = setInterval(() => {
        setCurrentIndex(prevIndex => (prevIndex + 1) % visibleAnnouncements?.length);
      }, 10000);
      return () => clearInterval(intervalId);
    } else {
      // Reset currentIndex if there are no visible announcements
      setCurrentIndex(0);
    }
  }, [visibleAnnouncements]);

  if (location.pathname !== DASHBOARD_PAGE_PATH || isFetching || !visibleAnnouncements?.length) {
    if (!isPreview) {
      return null;
    }
  }

  const {
    ab_bgcolor = '',
    ab_fontcolor = '',
    ab_content = '',
    ab_dismissible = true,
    ab_id = 0
  } = visibleAnnouncements[currentIndex] ?? {};

  return (
    <Navbar
      id="announcementBarId"
      className={`ql-snow ${bem.b('')} ${visibleAnnouncements?.length > 0 ? 'show' : 'no-transition'} ${
        isPreview === true ? 'preview' : ''
      }`}
      style={{
        backgroundColor: ab_bgcolor,
        color: '#000'
      }}
    >
      <div
        className="ql-editor p-0"
        style={{ flexGrow: 1 }}
        dangerouslySetInnerHTML={{ __html: ab_content || '<p>Content Preview</p>' }}
      />

      {Boolean(ab_dismissible) && (
        <i
          className="fa fa-times-circle"
          title="Dismiss"
          aria-hidden="true"
          onClick={() => handleDismiss(ab_id)}
          style={{
            cursor: 'pointer',
            color: ab_fontcolor,
            fontSize: '1.5rem',
            padding: '0 10px'
          }}
        />
      )}
    </Navbar>
  );
};

AnnouncementBar.displayName = 'AnnouncementBar';
export default memo(withRoutingProps(AnnouncementBar));
